import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Icon from "@/components/Icon";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Search, ArrowRight, MapPin, Sparkles, ShieldCheck, MessageSquare, Star, Flame } from "lucide-react";
import SearchAutocomplete from "@/components/SearchAutocomplete";
import LegacyMarquee from "@/components/LegacyRibbon";

const FEATURED = new Set(["Mumbai", "Pune", "Nagpur"]);

export default function Landing() {
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  const [stats, setStats] = useState(null);
  const [query, setQuery] = useState("");
  const [geoCity, setGeoCity] = useState(null);
  const [bazaar, setBazaar] = useState(null);

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data));
    api.get("/stats/public").then((r) => setStats(r.data));
    api.get("/bazaar").then((r) => setBazaar(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!navigator.geolocation || !cities.length) return;
    navigator.geolocation.getCurrentPosition(
      () => setGeoCity("Pune"), // best-effort hint; nearest-city resolution can be added later
      () => {},
      { timeout: 4000 }
    );
  }, [cities.length]);

  return (
    <div className="grain">
      <LegacyMarquee />
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-border/60 px-3 py-1 text-xs uppercase tracking-[0.2em] text-muted-foreground bg-card/60">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" /> {t("hero.badge")}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
              {t("hero.title_1")}{" "}
              <span className="font-wordmark text-primary">{t("hero.title_brand")}</span> {t("hero.title_2")}
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
              {t("hero.subtitle")}
            </p>

            <div className="max-w-xl">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!user) return navigate("/login");
                  const p = new URLSearchParams();
                  if (query.trim()) p.set("q", query.trim());
                  navigate(`/items${p.toString() ? "?" + p.toString() : ""}`);
                }}
                className="flex items-center gap-2 bg-background border border-border/60 rounded-2xl p-2"
                data-testid="hero-search-form"
              >
                <Search className="h-4 w-4 shrink-0 ml-1 text-muted-foreground" />
                <Input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("hero.search_placeholder")}
                  className="border-0 shadow-none focus-visible:ring-0 h-10"
                  data-testid="hero-search-input"
                />
                <Button
                  type="submit"
                  className="rounded-xl h-10 px-5 text-white"
                  data-testid="hero-search-submit"
                >
                  {t("common.search")}
                </Button>
              </form>
            </div>

            <div className="flex flex-wrap gap-3 pt-2">
              {!user ? (
                <>
                  <Button onClick={() => navigate("/register")} size="lg" className="rounded-full" data-testid="hero-invite-btn">
                    {t("hero.invite")}
                  </Button>
                  <Button variant="outline" size="lg" className="rounded-full" onClick={() => navigate("/login")} data-testid="hero-login-btn">
                    {t("hero.member_login")}
                  </Button>
                </>
              ) : (
                <Button onClick={() => navigate("/cities")} size="lg" className="rounded-full" data-testid="hero-explore-btn">
                  {t("hero.explore")}
                </Button>
              )}
            </div>

            {stats && (
              <div className="grid grid-cols-4 gap-4 max-w-lg pt-6">
                {[
                  [t("stats.vendors"), stats.total_vendors],
                  [t("stats.members"), stats.total_users],
                  [t("stats.cities"), stats.total_cities],
                  [t("stats.categories"), stats.total_categories],
                ].map(([label, n]) => (
                  <div key={label} className="text-left">
                    <div className="font-heading text-2xl">{n}+</div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mt-1">
                      {label}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-5 relative">
            {/* Mandai / Bazaar visual — a stylised stack of stall cards (no external image dependency) */}
            <div className="relative aspect-[4/5]" data-testid="hero-mandai-visual">
              <div className="absolute inset-0 rounded-[2.5rem] bg-gradient-to-br from-primary/15 via-accent/10 to-primary/5 border border-border/60 overflow-hidden">
                {/* Decorative diagonal stripes — bunting feel */}
                <div className="absolute inset-x-0 top-6 h-4 flex gap-1.5 px-6 opacity-80">
                  {[ "#ef4444", "#f97316", "#eab308", "#22c55e", "#0ea5e9", "#a855f7", "#ec4899", "#f43f5e" ].map((c, i) => (
                    <span key={i} className="flex-1 rounded-b-md" style={{ background: c }} />
                  ))}
                </div>

                {/* Stall cards */}
                <div className="absolute inset-0 pt-20 pb-8 px-6 grid grid-cols-2 grid-rows-2 gap-4">
                  {[
                    { name: "Mithai Mandai", icon: "Cookie",          color: "#dc2626", items: ["Modak", "Karanji", "Pedha"] },
                    { name: "Sabji Mandai",  icon: "Carrot",          color: "#16a34a", items: ["Methi", "Bhindi", "Tomato"] },
                    { name: "Masala Mandai", icon: "Flame",           color: "#f97316", items: ["Haldi", "Garam", "Mirchi"] },
                    { name: "Vastra Mandai", icon: "Shirt",           color: "#a855f7", items: ["Paithani", "Khan", "Kashta"] },
                  ].map((s, i) => (
                    <div
                      key={s.name}
                      className="relative rounded-2xl bg-card border border-border/70 shadow-sm p-3 flex flex-col justify-between"
                      style={{ transform: `rotate(${(i % 2 === 0 ? -1 : 1) * 1.2}deg)` }}
                      data-testid={`mandai-stall-${i}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="h-8 w-8 rounded-xl flex items-center justify-center" style={{ background: `${s.color}22`, color: s.color }}>
                          <Icon name={s.icon} className="h-4 w-4" />
                        </span>
                        <div className="font-heading text-sm leading-tight">{s.name}</div>
                      </div>
                      <div className="space-y-1 mt-2">
                        {s.items.map((it) => (
                          <div key={it} className="text-[11px] flex items-center justify-between text-muted-foreground">
                            <span>{it}</span>
                            <span className="text-[10px] uppercase tracking-[0.16em]">fresh</span>
                          </div>
                        ))}
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-primary mt-2">Open today</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Floating tag */}
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2">
                <Card className="glass px-4 py-2 border-primary/30 inline-flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <span className="text-sm font-semibold">Aaj chi Mandai</span>
                  <span className="text-xs text-muted-foreground">· Members-only</span>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CITY GRID */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2">{t("section.browse_city_eyebrow")}</div>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight">
              {t("section.browse_city_title")}
            </h2>
            {geoCity && (
              <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5" /> {t("section.nearest")} <strong>{geoCity}</strong>
              </p>
            )}
          </div>
          <Link to="/cities" className="text-sm text-primary hover:underline" data-testid="see-all-cities">
            {t("section.see_all_cities").replace("{n}", cities.length)}
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4 stagger">
          {cities.slice(0, 18).map((c) => {
            const big = FEATURED.has(c.name);
            return (
              <Link
                key={c.name}
                to={user ? `/categories?city=${encodeURIComponent(c.name)}` : "/login"}
                data-testid={`city-select-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={`group relative overflow-hidden rounded-2xl border border-border/60 bg-card p-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${
                  big ? "sm:col-span-2 sm:row-span-1 md:row-span-1" : ""
                }`}
              >
                <div
                  className="h-12 w-12 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110"
                  style={{ background: `${c.color}22`, color: c.color }}
                >
                  <Icon name={c.icon} className="h-6 w-6" />
                </div>
                <div className="mt-3 font-semibold text-sm sm:text-base">{c.name}</div>
                <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground mt-1">
                  {t("common.explore")}
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* INDIAN BAZAAR */}
      {bazaar && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 border-t border-border/60" data-testid="bazaar-section">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2 flex items-center gap-1.5">
                <Flame className="h-3.5 w-3.5" /> {t("section.bazaar_eyebrow")}
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight">
                {t("section.bazaar_title_1")} <span className="font-wordmark text-primary">{t("section.bazaar_title_brand")}</span>
              </h2>
              <p className="text-muted-foreground mt-2 max-w-2xl">
                {t("section.bazaar_subtitle")}
              </p>
            </div>
          </div>

          {/* Trending items chip-row → quick item searches */}
          {!!bazaar.trending_items?.length && (
            <div className="mb-8" data-testid="bazaar-trending-items">
              <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground mb-2">{t("section.bazaar_popular")}</div>
              <div className="flex flex-wrap gap-2">
                {bazaar.trending_items.map((it) => (
                  <Link
                    key={`${it.category}-${it.name}`}
                    to={user ? `/vendors?search=${encodeURIComponent(it.name)}` : "/login"}
                    data-testid={`bazaar-item-${it.name.replace(/\s+/g, "-").toLowerCase()}`}
                    className="group flex items-center gap-2 rounded-full border border-border/60 bg-card hover:bg-primary/5 hover:border-primary/40 transition-colors px-3 py-1.5"
                  >
                    <span className="h-6 w-6 rounded-full bg-primary/15 text-primary flex items-center justify-center">
                      <Icon name={it.icon || "Tag"} className="h-3.5 w-3.5" />
                    </span>
                    <span className="text-sm font-medium">{it.name}</span>
                    <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground group-hover:text-primary">→</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Featured vendors */}
          {!!bazaar.featured_vendors?.length && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger" data-testid="bazaar-featured-vendors">
              {bazaar.featured_vendors.map((v) => (
                <Card key={v.vendor_id} className="overflow-hidden border-border/60 transition-all hover:-translate-y-1 hover:shadow-lg">
                  <Link to={user ? `/vendors/${v.vendor_id}` : "/login"} className="block">
                    <div className="relative h-36 bg-muted">
                      {v.banner && (
                        <img
                          src={v.banner}
                          alt={v.business_name}
                          onError={(e) => { e.currentTarget.style.background = "linear-gradient(135deg,#f97316,#eab308,#16a34a)"; e.currentTarget.removeAttribute("src"); }}
                          className="h-full w-full object-cover"
                        />
                      )}
                      <Badge className="absolute top-3 left-3 bg-primary text-primary-foreground gap-1">
                        <Sparkles className="h-3 w-3" /> {t("common.featured")}
                      </Badge>
                    </div>
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-heading text-lg leading-tight">{v.business_name}</div>
                        <div className="flex items-center gap-1 text-sm">
                          <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" /> {v.rating?.toFixed(1)}
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                        <Badge variant="secondary" className="font-normal">{v.category}</Badge>
                        <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {v.city}</span>
                      </div>
                    </div>
                  </Link>
                </Card>
              ))}
            </div>
          )}
        </section>
      )}

      {/* WHY US */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 border-t border-border/60">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              icon: ShieldCheck,
              title: t("whyus.referral_title"),
              text: t("whyus.referral_text"),
            },
            {
              icon: MessageSquare,
              title: t("whyus.talk_title"),
              text: t("whyus.talk_text"),
            },
            {
              icon: Sparkles,
              title: t("whyus.roots_title"),
              text: t("whyus.roots_text"),
            },
          ].map((f) => (
            <Card key={f.title} className="p-6 border-border/60">
              <f.icon className="h-6 w-6 text-primary" />
              <h3 className="font-heading text-xl mt-4">{f.title}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.text}</p>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}
